import {
    SyncOutlined,
    ThunderboltOutlined,
    VerticalAlignBottomOutlined,
} from '@ant-design/icons';
import { Button, message, Popconfirm, Radio } from 'antd';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import type Ad from 'stores/Ad';
import { maybeAddChangeListener } from 'stores/DynamicProperties/changeListeners';
import type DynamicPropertiesManager from 'stores/DynamicProperties/Manager';
import { DynamicPropertyStatus } from 'types/DynamicPropertyStatus';
import { type PropertyManagerType } from 'types/PropertyManagerType';
import { useForceUpdate } from 'utils/Hooks';

const ToolbarButton = styled(Button)`
    align-items: center;
    margin-right: 0.25em;
    padding: 0 0.75em;
`;

const RadioButton = styled(Radio.Button)`
    padding: 0 0.75em;
`;

const FlexFillSpacer = styled.div`
    flex: 1;
`;

const Label = styled.span`
    margin-right: 20px;
`;

const Container = styled.div`
    display: flex;
    align-items: center;
`;

export interface PropertiesDrawerToolbarProps {
    tabMode?: PropertyManagerType;
    currentPropertiesManager?: DynamicPropertiesManager | undefined;
    currentAd?: Ad | undefined;
    handleTabChange: (tabMode: PropertyManagerType) => void;
}

function PropertiesDrawerToolbar({
    tabMode,
    currentPropertiesManager,
    currentAd,
    handleTabChange,
}: PropertiesDrawerToolbarProps) {
    const [confirmVisible, setConfirmVisible] = useState(false);
    const forceUpdate = useForceUpdate();

    useEffect(
        () => maybeAddChangeListener(currentPropertiesManager, forceUpdate),
        [currentPropertiesManager, forceUpdate]
    );

    const changed = currentPropertiesManager?.getChanged() ?? false;

    const isLoading =
        currentPropertiesManager?.status === DynamicPropertyStatus.LOADING;

    const handleSave = () => {
        if (currentPropertiesManager?.getChanged()) {
            void currentPropertiesManager.parentDocument.saveDynamicProperties(
                true
            );
        }
    };

    const handleRevert = () => {
        setConfirmVisible(false);
        if (currentPropertiesManager?.getChanged()) {
            currentPropertiesManager.revert();
        }
    };

    const handleTeleport = async () => {
        try {
            const properties = currentAd?.properties;
            if (properties) {
                await properties.promptAndLoadFigmaDataFile();
            }
        } catch (error) {
            void message.error('Failed to teleport from Figma: ' + error);
        }
    };

    return (
        <Container>
            <Label>Properties</Label>
            <Radio.Group
                value={tabMode}
                onChange={(e) => {
                    handleTabChange(e.target.value);
                }}
            >
                <RadioButton disabled={!currentAd} value="Ad">
                    Ad
                </RadioButton>
                <RadioButton disabled={!currentAd} value="Template">
                    Template
                </RadioButton>
                <RadioButton value="Creative">Creative</RadioButton>
            </Radio.Group>

            <FlexFillSpacer />

            <div>
                <ToolbarButton onClick={handleTeleport} disabled={isLoading}>
                    <ThunderboltOutlined /> Teleport
                </ToolbarButton>
                <Popconfirm
                    title={
                        <>
                            Revert all changes?
                            <br />
                            This will discard any unsaved changes and cannot be
                            undone.
                        </>
                    }
                    open={confirmVisible}
                    onConfirm={handleRevert}
                    onCancel={() => {
                        setConfirmVisible(false);
                    }}
                    okText="Revert"
                    okType="danger"
                    cancelText="Cancel"
                    placement="bottomLeft"
                >
                    <ToolbarButton
                        onClick={() => setConfirmVisible(true)}
                        disabled={!changed || isLoading}
                    >
                        <SyncOutlined /> Revert
                    </ToolbarButton>
                </Popconfirm>
                <ToolbarButton
                    onClick={handleSave}
                    disabled={!changed || isLoading}
                >
                    <VerticalAlignBottomOutlined /> Save
                </ToolbarButton>
            </div>
        </Container>
    );
}

export default observer(PropertiesDrawerToolbar);
